import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiDelete, apiDownload, ApiError } from '../api/client';
import { BUSINESSES } from '../domain/businesses';
import { formatMkd, formatDate, formatNumber, todayIso, monthAgoIso } from '../lib/format';
import FilterBar, { btn } from '../components/FilterBar';
import PagesaModal from '../components/PagesaModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Pagesat — every expense across all four businesses in one list.
 *
 * Ports frmPagesatAktuale and frmPagesatPlanifikuara, which are the same
 * screen over different tables, so one component serves both via `variant`.
 *
 * The business filter is new: the desktop screen could only search by typing a
 * business name into the same box as everything else.
 */

interface PagesaRow {
  ID: number;
  data: string;
  kategoria: string | null;
  artikulli: string | null;
  biznesi: string;
  sasia: number | null;
  periudha: string | null;
  cmimi: number;
  hasFile: number;
}

export default function Pagesat({ variant }: { variant: 'aktuale' | 'planifikuara' }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [biznesiId, setBiznesiId] = useState<number | ''>('');
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [deleting, setDeleting] = useState<PagesaRow | null>(null);

  const listQuery = useQuery({
    queryKey: ['pagesat', variant, search, startDate, endDate, biznesiId],
    queryFn: () =>
      apiGet<{ rows: PagesaRow[]; total: number }>(`/pagesat/${variant}`, {
        search,
        startDate,
        endDate,
        biznesiId: biznesiId === '' ? undefined : biznesiId,
      }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['pagesat'] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/pagesat/${variant}/${id}`),
    onSuccess: () => {
      toast.success('Rreshti u fshi me sukses.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  async function download(path: string, params?: Record<string, unknown>) {
    try {
      await apiDownload(path, params);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Shkarkimi dështoi.');
    }
  }

  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;
  const isAktuale = variant === 'aktuale';

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">
          {isAktuale ? 'Pagesat aktuale' : 'Pagesat e planifikuara'}
        </h1>
        <p className="text-sm text-muted">Të gjitha bizneset në një listë</p>
      </div>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko kategori, artikull, periudhë ose biznes"
      >
        <div>
          <label className="mb-1 block text-xs font-medium text-muted" htmlFor="pagesat-biznesi">
            Biznesi
          </label>
          <select
            id="pagesat-biznesi"
            value={biznesiId}
            onChange={(e) => setBiznesiId(e.target.value === '' ? '' : Number(e.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            <option value="">Të gjitha</option>
            {BUSINESSES.map((b, i) => (
              <option key={b.slug} value={i + 1}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() =>
            download(`/pagesat/${variant}/eksport`, {
              search,
              startDate,
              endDate,
              biznesiId: biznesiId === '' ? undefined : biznesiId,
            })
          }
          disabled={rows.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
        <button type="button" onClick={() => setEditing({ id: null })} className={btn.primary}>
          Shto rresht
        </button>
      </FilterBar>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Kategoria</th>
                <th className="px-4 py-3 font-semibold">Artikulli</th>
                <th className="px-4 py-3 font-semibold">Biznesi</th>
                {isAktuale && <th className="px-4 py-3 text-right font-semibold">Sasia</th>}
                <th className="px-4 py-3 font-semibold">Periudha</th>
                <th className="px-4 py-3 text-right font-semibold">Çmimi</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={isAktuale ? 9 : 8} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={isAktuale ? 9 : 8} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna për këtë periudhë.
                  </td>
                </tr>
              )}

              {rows.map((row, index) => (
                <tr
                  key={row.ID}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                >
                  <td className="px-4 py-2.5 text-muted">{index + 1}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.data)}</td>
                  <td className="px-4 py-2.5">{row.kategoria ?? 'N/A'}</td>
                  <td className="px-4 py-2.5">{row.artikulli ?? 'N/A'}</td>
                  <td className="px-4 py-2.5 text-muted">{row.biznesi}</td>
                  {isAktuale && (
                    <td className="tnum px-4 py-2.5 text-right">
                      {row.sasia == null ? '' : formatNumber(row.sasia)}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-muted">{row.periudha ?? ''}</td>
                  <td className="tnum px-4 py-2.5 text-right font-medium">
                    {formatMkd(row.cmimi)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      {isAktuale && Number(row.hasFile) === 1 && (
                        <button
                          type="button"
                          onClick={() => download(`/pagesat/aktuale/rreshti/${row.ID}/fatura`)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50"
                        >
                          Fatura
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditing({ id: row.ID })}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Fshij
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-muted">
            Shuma totale nga {formatDate(startDate)} deri më {formatDate(endDate)}:
          </span>
          <span className="tnum text-lg font-semibold text-ink">{formatMkd(total)}</span>
        </div>
      </div>

      {editing && (
        <PagesaModal
          variant={variant}
          rowId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Të dhënat u ruajtën me sukses!');
            refresh();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message="A jeni i sigurt që dëshironi të fshini këtë rresht?"
          confirmLabel="Fshij"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.ID);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
